import { YodleeQuery } from '@1bill-app/services/yodlee/yodlee.query';
import { YodleeService } from '@1bill-app/services/yodlee/yodlee.service';
import { YodleeStore } from '@1bill-app/services/yodlee/yodlee.store';
import { YodleeAccount } from '@1bill-app/services/yodlee/yodlee.types';
import { Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { SubSink } from 'subsink';
import { HomeService } from '../../../../services/home/home.service';
import { Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';
import {
  ROUTE_HOMEPAGE,
  ROUTE_CASH_SCREEN,
  ROUTE_PAYMENT_OPTIONS,
} from '@1bill-app/constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-payment-settings',
  templateUrl: './payment-settings.page.html',
  styleUrls: ['./payment-settings.page.scss'],
})
export class PaymentSettingsPage implements OnInit {
  constructor(
    private homeService: HomeService,
    private yodleeQuery: YodleeQuery,
    private yodleeStore: YodleeStore,
    private logger: NGXLogger,
    private yodleeService: YodleeService,
    private route: ActivatedRoute,
    private router: Router,
    private loadingController: LoadingController,
    private ngZone: NgZone,
    private alertController: AlertController,
  ) {}

  public homeDataEvent$ = this.homeService.homeDataEvent$;
  public isPullToRefresh: boolean;

  subs = new SubSink();
  id: number;
  title: string;
  public loading$ = this.yodleeQuery.isLoading$;

  accountDetails$ = this.yodleeQuery.accountDetails$;
  accountDetails: YodleeAccount;
  private getAccountDetailsSubscription: Subscription;

  accType: string;
  readonly ROUTE_PAYMENT_OPTIONS = ROUTE_PAYMENT_OPTIONS;

  get subtitle() {
    if (!this.accountDetails) return '';
    else if (this.accountDetails?.isManualAccount) return this.accountDetails.accountType;
    return 'Acc. Number: ' + this.accountDetails.accountNumber;
  }

  get defaultHref() {
    if (this.accountDetails) return `${ROUTE_CASH_SCREEN}?type=` + this.accountDetails.type;
    else return ROUTE_HOMEPAGE;
  }

  async ngOnInit() {
    this.subs.sink = this.yodleeService.getAccounts().subscribe();
    this.id = Number(this.route.snapshot.params?.id);
    if (this.id) this.yodleeStore.update({ currentAccountId: this.id });
    this.getAccountDetailsSubscription = this.accountDetails$.subscribe(
      async (accountDetails) => {
        this.accountDetails = accountDetails;
        this.accType = this.accountDetails?.accountType.toLowerCase();
      },
    );
  }

  handleLinkBankAccount() {
    this.homeService.presentLinkBankAccountModal();
  }

  navigate() {
    this.router.navigate(['add-edit-manual-account', this.accountDetails.id]);
  }

  doRefresh(event) {
    //this.isPullToRefresh = true;
    this.yodleeService
      .getAccounts()
      .toPromise()
      .then(() => {
        //this.isPullToRefresh = false;
        event.target.complete();
      })
      .catch((err) => {
        this.logger.log(err);
        event.target.complete();
        //this.isPullToRefresh = false;
      });
  }

  async presentAlertUnlink() {
    const title = 'Are you sure you want to unlink this account?';
    const message = 'You can always re-link your account on the settings page.';
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert alert-icon',
      message: `<img src="assets/icon/alert/warning.svg" alt="warning-icon" /><h2 class="alert-title">${title}</h2><p class="alert-text">${message}</p>`,
      buttons: [
        {
          cssClass: 'bill-alert alert-icon yes-btn',
          text: 'Yes',
          handler: () => this.ngZone.run(() => this.handleUnlinkAccount()),
        },
        {
          cssClass: 'bill-alert alert-icon no-btn',
          text: 'Cancel',
          role: 'danger',
        },
      ],
    });

    await alert.present();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  async handleUnlinkAccount() {
    const loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Please wait...',
      //duration: 2000,
    });
    await loading.present();
    try {
      // Unsubscribe needed so that the subscription in ngOnInit does not cause
      // accountDetails and accType to be undefined.
      this.getAccountDetailsSubscription.unsubscribe();
      await this.yodleeService.unlinkAccount(this.accountDetails?.id).toPromise();

      await Promise.all([
        this.yodleeService.getAccounts().toPromise(),
        this.yodleeService.getDashboardWealthData().toPromise(),
        this.yodleeService.getWealthInformation().toPromise(),
      ]);
      this.yodleeQuery.accountListUpdated$.next(
        this.accountDetails?.accountNumber ?? String(this.accountDetails?.id) ?? 'Unlink',
      );

      //this.subs.sink = this.yodleeService.getAccounts().subscribe();
      //this.id = Number(this.route.snapshot.queryParams?.id);
      if (this.id) this.yodleeStore.update({ currentAccountId: this.id });

      await this.router.navigate([ROUTE_CASH_SCREEN], {
        queryParams: { type: this.accType },
      });
    } catch (error) {
      this.logger.error('Error unlink account: ', error);
      this.errorAlert();
    } finally {
      await loading.dismiss();
    }
  }

  async errorAlert() {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert alert-icon',
      message:
        '<img src="assets/icon/alert/warning.svg" alt="warning-icon" /><h2 class="alert-title">Oops!</h2><p class="alert-text">Something went wrong! Please try again later.</p>',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Got it',
        },
      ],
    });

    await alert.present();
  }
}
